import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
// Define the LicenseRequest interface directly instead of importing
interface LicenseRequest {
  id: number;
  userId?: number;
  name: string;
  email: string;
  projectType: string;
  usageDuration: string;
  tracks: string[];
  description: string;
  status: string;
  aiAnalysis?: string;
  invoiceId?: string;
  amount?: number;
  referralCode?: string;
  fileFormat: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Check,
  Clock,
  Filter,
  Loader2,
  Search,
  X,
  AlertTriangle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";

export default function AdminRequests() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();
  const [searchParams, setSearchParams] = useState(new URLSearchParams(location.split("?")[1]));
  
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<LicenseRequest | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Get request ID from URL if present
  useEffect(() => {
    const params = new URLSearchParams(location.split("?")[1]);
    const id = params.get('id');
    if (id) {
      // Fetch the specific request
      fetch(`/api/license-requests/${id}`)
        .then(res => res.json())
        .then(data => {
          setSelectedRequest(data);
          setIsDetailsOpen(true);
        })
        .catch(err => {
          toast({
            title: "Error loading request",
            description: "Could not load the requested license request.",
            variant: "destructive",
          });
        });
    }
  }, [location, toast]);

  // Fetch all license requests
  const { data: licenseRequests = [], isLoading } = useQuery<LicenseRequest[]>({
    queryKey: ['/api/license-requests'],
  });

  // Update license request status mutation
  const updateRequestStatus = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      setIsUpdating(true);
      try {
        const response = await apiRequest("PATCH", `/api/license-requests/${id}`, { status });
        return await response.json();
      } finally {
        setIsUpdating(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/license-requests'] });
      toast({
        title: "Status Updated",
        description: "License request status has been updated successfully.",
      });
      setIsDetailsOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "There was an error updating the license request status.",
        variant: "destructive",
      });
    },
  });

  // Filter requests based on selected filter and search query
  const filteredRequests = licenseRequests.filter((request) => {
    // Filter by status
    if (filter !== "all" && request.status !== filter) {
      return false;
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        request.name.toLowerCase().includes(query) ||
        request.email.toLowerCase().includes(query) ||
        request.description.toLowerCase().includes(query) ||
        request.tracks.some(track => track.toLowerCase().includes(query))
      );
    }

    return true;
  });

  // Handle request status change
  const handleStatusChange = (id: number, status: string) => {
    updateRequestStatus.mutate({ id, status });
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    let variant = "";
    
    switch (status) {
      case "approved":
        variant = "success";
        break;
      case "pending":
        variant = "warning";
        break;
      case "rejected":
        variant = "destructive";
        break;
      default:
        variant = "secondary";
    }
    
    return (
      <Badge variant={variant as any}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Fix type errors by adding explicit types and null checks
  const aiResult = selectedRequest?.aiAnalysis ? JSON.parse(selectedRequest.aiAnalysis as string) : {};
  const score = aiResult.score || 0;
  const recommendation = aiResult.recommendation || '';
  const reason = aiResult.reason || '';
  const flagged = aiResult.flagged || false;
  const flagReason = aiResult.flagReason || '';

  // Fix the badge variant type error
  const getBadgeVariant = (rec: string): "default" | "destructive" | "outline" | "secondary" => {
    if (rec.includes('approve')) return 'default';
    if (rec.includes('reject')) return 'destructive';
    return 'secondary';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Link href="/admin">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">License Requests</h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>All Requests</CardTitle>
              <CardDescription>View and manage all license requests</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search requests..."
                  className="pl-8 w-[200px] md:w-[300px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select defaultValue="all" onValueChange={setFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No license requests found</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Project Type</TableHead>
                    <TableHead>Tracks</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{request.id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{request.name}</p>
                          <p className="text-sm text-muted-foreground">{request.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {request.projectType === 'social' ? 'Social Media' : 
                         request.projectType === 'youtube' ? 'YouTube' : 
                         request.projectType === 'tv' ? 'TV Show' : 
                         request.projectType === 'film' ? 'Film/Movie' : 
                         request.projectType === 'corporate' ? 'Corporate' : 
                         request.projectType}
                      </TableCell>
                      <TableCell>{request.tracks.length}</TableCell>
                      <TableCell>{formatDate(request.createdAt.toString())}</TableCell>
                      <TableCell>
                        <StatusBadge status={request.status} />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setIsDetailsOpen(true);
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Details Dialog */}
      {selectedRequest && (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>License Request Details</DialogTitle>
              <DialogDescription>
                Review the license request and update its status
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 my-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Name</h4>
                  <p>{selectedRequest.name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Email</h4>
                  <p>{selectedRequest.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Project Type</h4>
                  <p>{selectedRequest.projectType === 'social' ? 'Social Media' : 
                      selectedRequest.projectType === 'youtube' ? 'YouTube' : 
                      selectedRequest.projectType === 'tv' ? 'TV Show' : 
                      selectedRequest.projectType === 'film' ? 'Film/Movie' : 
                      selectedRequest.projectType === 'corporate' ? 'Corporate' : 
                      selectedRequest.projectType}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Usage Duration</h4>
                  <p>{selectedRequest.usageDuration === 'one-time' ? 'One-time Use' : 
                      selectedRequest.usageDuration === 'six-months' ? '6 Months' : 
                      selectedRequest.usageDuration === 'one-year' ? '1 Year' : 
                      'Perpetual'}</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Tracks</h4>
                <div className="mt-1 space-y-1">
                  {selectedRequest.tracks.map((track, index) => (
                    <Badge key={index} variant="outline" className="mr-1 mb-1">{track}</Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
                <p className="mt-1 text-sm whitespace-pre-line">{selectedRequest.description}</p>
              </div>
              
              {selectedRequest.aiAnalysis && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">AI Score:</span>
                    <span>{score} / 10</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Recommendation:</span>
                    <Badge variant={getBadgeVariant(recommendation)}>
                      {recommendation}
                    </Badge>
                  </div>
                  
                  <div>
                    <span className="font-medium">Reasoning:</span>
                    <p className="mt-1 text-sm">{reason}</p>
                  </div>
                  
                  {flagged && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-700">Flagged for Review</p>
                          <p className="mt-1 text-sm text-red-600">{flagReason}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Current Status</h4>
                <div className="mt-1">
                  <StatusBadge status={selectedRequest.status} />
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex flex-col sm:flex-row sm:justify-between sm:space-x-2">
              <div className="flex space-x-2 mb-3 sm:mb-0">
                <Button
                  variant="outline"
                  onClick={() => setIsDetailsOpen(false)}
                  className="flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="destructive"
                  onClick={() => handleStatusChange(selectedRequest.id, "rejected")}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <X className="mr-2 h-4 w-4" />
                  )}
                  Reject
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange(selectedRequest.id, "pending")}
                  disabled={isUpdating || selectedRequest.status === "pending"}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Pending
                </Button>
                <Button
                  onClick={() => handleStatusChange(selectedRequest.id, "approved")}
                  disabled={isUpdating}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isUpdating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Approve
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
